import { useRef,useState, useEffect, } from "react"
import { isMobile } from "react-device-detect";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import { Mail, Eye, EyeOff, ArrowRight } from "lucide-react"
import axios from "axios";
import AuthSlider from "../auth-slider";


function NewFPEmailForm() {
  const [searchParams] = useSearchParams();
  const { token } = useParams();
  const emailRef = useRef(null);
  const newPasswordRef = useRef(null);
  const confirmPasswordRef = useRef(null);
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  const API = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const emailFromUrl = searchParams.get("email");
    
    if (emailFromUrl && emailRef.current) {
      emailRef.current.value = decodeURIComponent(emailFromUrl);
    } else if (token) {
      try {
        const base64Url = token.split('.')[1];
        if (base64Url) {
          const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
          const decodedPayload = JSON.parse(atob(base64));
          if (decodedPayload.email && emailRef.current) {
            emailRef.current.value = decodedPayload.email;
          }
        }
      } catch (e) {
        // Error handling without console.log
      }
    }
    
    if (token && newPasswordRef.current) {
      newPasswordRef.current.focus();
    }
  }, [searchParams, token]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePassword = (password) => {
    if (password.length < 8) return "Password must be at least 8 characters long";
    if (!/[A-Z]/.test(password)) return "Password must contain at least one uppercase letter";
    if (!/[a-z]/.test(password)) return "Password must contain at least one lowercase letter";
    if (!/\d/.test(password)) return "Password must contain at least one number";
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) 
      return "Password must contain at least one special character";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setEmailError("");
    setPasswordError("");
    setConfirmPasswordError("");
    setSuccess("");
    
    const email = emailRef.current.value;
    const newPassword = newPasswordRef.current.value;
    const confirmPassword = confirmPasswordRef.current.value;

    let hasError = false;

    if (!email) {
      setEmailError("Email is required");
      emailRef.current.focus();
      hasError = true;
    } else if (!validateEmail(email)) {
      setEmailError("Please enter a valid email address");
      emailRef.current.focus();
      hasError = true;
    }

    if (!newPassword) {
      setPasswordError("New password is required");
      newPasswordRef.current.focus();
      hasError = true;
    } else {
      const pwdError = validatePassword(newPassword);
      if (pwdError) {
        setPasswordError(pwdError);
        newPasswordRef.current.focus();
        hasError = true;
      }
    }

    if (!confirmPassword) {
      setConfirmPasswordError("Please confirm your password");
      confirmPasswordRef.current.focus();
      hasError = true;
    } else if (newPassword !== confirmPassword) {
      setConfirmPasswordError("Passwords do not match");
      confirmPasswordRef.current.focus();
      hasError = true;
    }

    if (!token) {
      setPasswordError("Invalid reset token. Please try requesting a new password reset.");
      hasError = true;
    }

    if (hasError) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.post(`${API}/api/password-reset`, {
        token: token,
        email,
        password: newPassword,
        password_confirmation: confirmPassword
      });
      
      setSuccess("Password reset successful!");
      setTimeout(() => {
        navigate('/login');
      }, 1500);
      
    } catch (error) {
      const errorMessage = error.response?.data?.message || 
        error.response?.status === 422 ? "Invalid or expired reset token. Please request a new password reset." :
        error.response?.status === 404 ? "Reset token not found. Please request a new password reset." :
        "Failed to reset password. Please try again.";
      
      setPasswordError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = () => {
    if (emailError) setEmailError("");
  };
  
  const handleNewPasswordChange = () => {
    if (passwordError) setPasswordError("");
  };
  
  const handleConfirmPasswordChange = () => {
    if (confirmPasswordError) setConfirmPasswordError("");
  };
  
  const toggleNewPasswordVisibility = () => {
    setShowNewPassword(!showNewPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <>
      <div className="min-vh-100 overflow-hidden">
        <div className="container-fluid h-100 p-0">
          <div className="row g-0 h-100 flex-nowrap">
            <AuthSlider/>
            <div className="col-lg-6 offset-lg-6 col-12 d-flex align-items-center justify-content-center bg-white" style={{ minHeight: isMobile ? "80vh":"100vh", overflowY: "auto" }}>
              <div className={`w-100 p-4 p-md-0`} style={{ maxWidth: "450px" }}>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div className="d-flex align-items-center">
                    <div
                      className="d-flex align-items-center justify-content-center me-3"
                    >
                      <img src="/assets/images/certificate/certificate_logo.png" width={180} height={40}/>
                    </div>
                  </div>
                </div>

                <div className="login-form">
                  <h3 className="mb-4 fw-bold text-balance" style={{ color: "#2D3748", fontSize: "2rem", lineHeight: "1.3" }}>
                    Reset Password</h3>
                  <p className="text-muted">Enter your new password</p>
                  <form onSubmit={handleSubmit}>
                    <div className="mb-3">
                      <label
                        htmlFor="email"
                        className="form-label fw-semibold mb-2"
                        style={{ color: "#4A5568", fontSize: "0.95rem" }}
                      >
                        Email <span style={{ color: "#260efcff" }}>*</span>
                      </label>
                      <div className="position-relative">
                        <input
                          type="email"
                          ref={emailRef}
                          className={`form-control form-control-lg pe-5 ${emailError ? 'is-invalid' : ''}`}
                          id="email"
                          name="email"
                          placeholder="Enter your email"
                          onChange={handleEmailChange}
                          disabled={!!(token || searchParams.get("email"))}
                          style={{
                            border: "2px solid #E2E8F0",
                            borderRadius: "12px",
                            padding: "14px 50px 14px 16px",
                            fontSize: "1rem",
                            transition: "border-color 0.2s ease",
                          }}
                          onFocus={(e) => (e.target.style.borderColor = "#260efcff")}
                          onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")}
                        />
                        {!emailError &&<Mail
                          size={20}
                          className="position-absolute top-50 end-0 translate-middle-y me-3"
                          style={{ color: "#A0AEC0" }}
                        />}
                        {emailError && <div className="invalid-feedback">{emailError}</div>}

                      </div>
                    </div>

                    <div className="mb-3">
                      <label
                        htmlFor="newPassword"
                        className="form-label fw-semibold mb-2"
                        style={{ color: "#4A5568", fontSize: "0.95rem" }}
                      >
                        New Password <span style={{ color: "#260efcff" }}>*</span>
                      </label>
                      <div className="position-relative">
                        <input
                          type={showNewPassword ? "text" : "password"}
                          className={`form-control form-control-lg pe-5 ${passwordError ? "is-invalid" : ""}`}
                          id="newPassword"
                          onChange={handleNewPasswordChange}
                          ref={newPasswordRef}
                          placeholder="Enter new password"
                          style={{
                            border: "2px solid #E2E8F0",
                            borderRadius: "12px",
                            padding: "14px 50px 14px 16px",
                            fontSize: "1rem",
                            transition: "border-color 0.2s ease",
                          }}
                          onFocus={(e) => (e.target.style.borderColor = "#260efcff")}
                          onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")}
                        />
                        {!passwordError &&
                        <button
                          type="button"
                          className="btn border-0 bg-transparent position-absolute top-50 end-0 translate-middle-y me-2"
                          onClick={toggleNewPasswordVisibility}
                          style={{ zIndex: 10 }}
                        >
                          {showNewPassword ? (
                            <EyeOff size={20} style={{ color: "#A0AEC0" }} />
                          ) : (
                            <Eye size={20} style={{ color: "#A0AEC0" }} />
                          )}
                        </button>}
                      </div>
                      {passwordError && (
                        <div className="text-danger mt-2">{passwordError}</div>
                      )}
                      <small className="text-muted">
                        Password must be at least 8 characters long and contain uppercase, lowercase, 
                        number and special character
                       </small>
                    </div>

                    <div className="mb-3">
                      <label
                        htmlFor="confirmPassword"
                        className="form-label fw-semibold mb-2"
                        style={{ color: "#4A5568", fontSize: "0.95rem" }}
                      >
                        Confirm Password <span style={{ color: "#260efcff" }}>*</span>
                      </label>
                      <div className="position-relative">
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          className={`form-control form-control-lg pe-5 ${confirmPasswordError ? "is-invalid" : ""}`}
                          id="confirmPassword"
                          placeholder="Confirm new password"
                          ref={confirmPasswordRef}
                          onChange={handleConfirmPasswordChange}
                          style={{
                            border: "2px solid #E2E8F0",
                            borderRadius: "12px",
                            padding: "14px 50px 14px 16px",
                            fontSize: "1rem",
                            transition: "border-color 0.2s ease",
                          }}
                          onFocus={(e) => (e.target.style.borderColor = "#260efcff")}
                          onBlur={(e) => (e.target.style.borderColor = "#E2E8F0")}
                        />
                        {!passwordError &&
                        <button
                          type="button"
                          className="btn border-0 bg-transparent position-absolute top-50 end-0 translate-middle-y me-2"
                          onClick={toggleConfirmPasswordVisibility}
                          style={{ zIndex: 10 }}
                        >
                          {showConfirmPassword ? (
                            <EyeOff size={20} style={{ color: "#A0AEC0" }} />
                          ) : (
                            <Eye size={20} style={{ color: "#A0AEC0" }} />
                          )}
                        </button>}
                        {confirmPasswordError && <div className="invalid-feedback">{confirmPasswordError}</div>}
                      </div>
                    </div>
                    {success && <div className="text-success">{success}</div>}

                    <button
                      type="submit"
                      className="btn btn-lg w-100 mb-4 text-white d-flex align-items-center justify-content-center fw-semibold"
                      style={{
                        background: "linear-gradient(135deg, #260efcff 0%, #2b05ffff 80%)",
                        backgroundColor: "#260efcff !important",
                        border: "none",
                        borderRadius: "12px",
                        padding: "14px",
                        fontSize: "1.1rem",
                        transition: "transform 0.2s ease, box-shadow 0.2s ease",
                      }}
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Processing...
                        </>
                      ) : (<>Reset Password <ArrowRight size={20} className="ms-2" /></>)}
                    </button>

                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default NewFPEmailForm
